import { APP_BASE_HREF, TitleCasePipe } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';

import moment from 'moment-timezone';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { MediaServiceApi } from '../../../../api/mediaService/media-service.api';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { LinkedinSubTypeEnum } from '../../../Basic/enums/linkedin-sub-type.enum';
import { TVEyesArchivedStateEnum } from '../../../../api/mentions/enum/tv-eyes-archived-state.enum';
import { MentionsApi } from '../../../../api/mentions/mentions.api';
import { PartialMentionApiModel } from '../../../../api/mentions/model/mention.model';
import { OEmbedRichResponseModel } from '../../../../api/oembed/models/oembed-rich-response.model';
import { OEmbedApi } from '../../../../api/oembed/oembed.api';
import { IndexHighlightPipe } from '../../../Misc/pipes/Highlight/indexHighlight.pipe';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { CurrentUserStateService, TargetingAccessLevel } from '../../../../Core/Users/services/current-user-state.service';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { TopicsService } from '../../../../Monitor/Core/services/topics.service';
import { mentionAttributeTypesEnum } from '../../../../Monitor/Shared/enums/mention-attribute-types.enum';
import { CriticalMentionMessageModel } from '../../../../Monitor/Shared/models/critical-mention-message.model';
import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { download } from '../../../Misc/helpers/download/download.helper';
import { KeywordHighlightModel } from '../../../Misc/models/keyword-highlight.model';
import { Mention } from '../../models/mention';
import { TVEyesMentionsService } from '../../services/tveyes-mentions.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { TranslateMentionsService } from '../../services/translate-mentions.service';
import { criticalMentionMessageTypesEnum } from '../../../../Monitor/Shared/enums/critical-mention-message-types.enum';
import { IframeDialogComponent } from '../../../Dialogs/components/IframeDialog/iframe-dialog.component';
import { TextInputDialogComponent } from '../../../Dialogs/components/TextInputDialog/text-input-dialog.component';
import { IframeDialogModel } from '../../../Dialogs/models/iframe-dialog.model';
import { TextInputDialogModel } from '../../../Dialogs/models/text-input-dialog.model';
import { MediaApi } from '../../../../api/contentLibrary/models/content-library-media.api.model';
import { ImageModel } from '../../../Misc/models/image.model';
import { ViewerUrlTypeEnum } from '@Common/Mentions/enums/viewer-url-type.enum';

enum TranslateTabIds {
    ORIGINAL,
    TRANSLATED
}

@Component({
    selector: 'agpr-mention-content',
    templateUrl: './mention-content.component.html',
    styleUrls: ['./mention-content.component.scss'],
    providers: [{
        provide: MediaServiceApi,
        useClass: MediaServiceApi
    }],
    standalone: false
})
export class MentionContentComponent implements OnInit, OnDestroy {

    @Input() id: string;
    @Input() highlightKeywords: KeywordHighlightModel[];
    @Input() showImageList: boolean = false;
    @Input() isTakeOverPanel: boolean = false;
    @Input() isLanguagePanelHalfWidth: boolean = false;
    @Input() showOriginalImageSize: boolean = false;
    @Input() useMentionCardErrorTemplate: boolean = false;

    @Output() outletClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() contactClicked: EventEmitter<void> = new EventEmitter<void>();

    preferences: PreferencesModel;
    user: UserModel;
    mention: Mention = null;

    dateFormat: string;

    bodyParagraphs: string[] = [];
    summaryParagraphs: string[] = [];

    isCriticalMention: boolean;
    isLegacyStashedClip: boolean;
    isStashedClip: boolean;
    isCriticalMentionValid: boolean;
    criticalMentionFeedbackMessage: string;
    iframeDialogKey: string;

    criticalMentionSlimPlayerURL: string;
    isSocialFrameLoaded: boolean;
    isLegacyTargetingUser: boolean;

    socialEmbedURL: string;
    socialEmbedCode: SafeHtml;

    search: MentionSearchModel;

    currentYear: number = new Date().getFullYear();
    lexisNexusResourceUrl: SafeResourceUrl;
    isTvEyes: boolean = false;

    window = window;

    attachments: MediaApi[] = [];

    iframeTitle: string;

    selectedTabIndex: number = 0;
    tabIds = TranslateTabIds;

    openAiPermission: boolean = false;

    canLinkOut: boolean = false;

    private subscriptions: Subscription[] = [];
    private titleCasePipe = new TitleCasePipe();

    readonly viewerUrlTypeEnum = ViewerUrlTypeEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly tvEyesState = TVEyesArchivedStateEnum;
    readonly endOfUrl = /([^\/]+$)/g;
    readonly linkedinTypeEnum = LinkedinSubTypeEnum;
    readonly mentionAttributeTypesEnum = mentionAttributeTypesEnum;

    private readonly totalValidDaysForCriticalMention: number = 60;

    constructor(
        public mentionsApi: MentionsApi,
        private localizationService: LocalizationService,
        private mentionsService: MentionsService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private sanitizer: DomSanitizer,
        private oembedApi: OEmbedApi,
        private coreDialogService: CoreDialogService,
        private currentUserStateService: CurrentUserStateService,
        private topicsService: TopicsService,
        private mediaServiceApi: MediaServiceApi,
        public httpClient: HttpClient,
        private cdr: ChangeDetectorRef,
        private tvEyesMentionsService: TVEyesMentionsService,
        protected indexHighlightPipe: IndexHighlightPipe,
        protected translateService: TranslateMentionsService,
        public injector: Injector
    ) {}

    ngOnInit() {
        this.subscriptions.push(
            this.mentionsService.getFocusedMention().pipe(distinctUntilChanged()).subscribe(
                mention => {
                    if (!mention) {
                        this.mention = null;
                        this.isCriticalMention = false;
                        this.criticalMentionSlimPlayerURL = '';
                        this.socialEmbedURL = undefined;
                        this.socialEmbedCode = undefined;
                        this.isSocialFrameLoaded = false;
                        this.lexisNexusResourceUrl = null;
                        this.bodyParagraphs = [];
                        this.summaryParagraphs = [];
                        this.isTvEyes = false;
                    } else {
                        let reloadSocial = false;
                        if (!this.mention || mention.id !== this.mention.id) {
                            this.trackMention(mention);
                        }
                        if (!this.mention || this.mention.url !== mention.url) {
                            this.isSocialFrameLoaded = false;
                            reloadSocial = true;
                            this.cdr.detectChanges();
                        }
                        this.mention = mention;
                        this.setCanLinkOut();
                        this.iframeTitle = this.localizationService.transform(
                            'Mention.details.iframeTitle',
                            {media: this.titleCasePipe.transform(this.mention.mediaType.id)}
                        );
                        const highlightedBody = this.indexHighlightPipe.transform(mention.body, mention.contexualKeywordIndices, this.highlightKeywords);
                        this.bodyParagraphs = (highlightedBody || '').split('\n').filter(x => x !== '');
                        this.summaryParagraphs = (mention.summary || '').split('\n').filter(x => x !== '');
                        this.setCriticalMentionFeatures();
                        this.isTvEyes = this.mention.isTvEyes();
                        this.loadAttachments();
                        if (reloadSocial) {
                            this.socialEmbedCode = undefined;
                            this.socialEmbedURL = this.mention.getEmbedURL();
                            if (!this.socialEmbedURL) {
                                this.getOEmbedCode();
                            }
                        }

                        // because the translated tab is disabled just before this, it seems to ignore switching tabs automatically, the setTimeout fixes it
                        setTimeout(() => {
                            if (this.mention?.isTranslatedPreferred && this.mention.translation) {
                                this.selectedTabIndex = 1;
                            } else {
                                this.selectedTabIndex = 0;
                            }
                        }, 0);
                    }
                }
            ),
            this.currentUserStateService.getTargetingAccessLevel().subscribe(x => {
                this.isLegacyTargetingUser = x === TargetingAccessLevel.CLASSIC_ONLY;
            }),
            this.topicsService.getFocusedTopic().subscribe(
                search => {
                    this.search = search;
                }
            ),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                prefs => {
                    this.preferences = prefs;
                    this.dateFormat = prefs.getDateFormatString(true, true, true);

                    this.setCriticalMentionFeatures();
                }
            ),
            this.currentUserStateService.getUser().subscribe(
                user => {
                    this.user = user;
                }
            ),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                }
            }),
        );
    }


    openTVEyesDialog() {
        if (!this.mention.url) {
            return;
        }

        const keywords: string[] = this.highlightKeywords
            ?.flatMap(x => x.keywords) ?? [];

        this.tvEyesMentionsService.openTVEyesDialog(this.mention, this.search, keywords).subscribe();
    }

    openTvEyesPodcastPlayer() {
        this.tvEyesMentionsService.openTvEyesPodcastPlayer(this.mention.tvEyesPodcastID, this.highlightKeywords);
    }




    /**
     * Tracks a mention as viewed in the api
     */
    trackMention(mention: Mention): void {
        // Only track mentions that have a mercuryId
        if (mention.id) {
            this.mentionsApi.trackMentionView(mention.convertToApiMention()).subscribe();
        }
    }


    editCriticalMention(): void {
        const styleOverrideURL = `https://${window.location.hostname}/app/assets/css/critical-mention-override.css`;

        const iframeConfig: IframeDialogModel = {
            inputs: {
                id: 'mention.wordplayEditor',
                heading: 'Mention.details.wordplayEditor',
                url: `${this.mention.criticalMentionWordPlayUrl}&ess=${styleOverrideURL}`
            },
            outputs: {
                message: (message) => {
                    this.handleCriticalMentionMessage(message);
                }
            }
        };
        this.iframeDialogKey = this.coreDialogService.open(IframeDialogComponent, iframeConfig);
    }

    loadAttachments(): void {
        this.attachments = [];

        if (this.mention.attachments.length > 0) {
            this.mediaServiceApi.getFiles(this.mention.attachments).subscribe(mediaResults => {
                mediaResults.mediaResults.forEach(mediaResult => {
                    this.attachments.push({
                        id: mediaResult.id,
                        name: mediaResult.name,
                        url: mediaResult.url,
                        mimeType: mediaResult.mimeType,
                        size: mediaResult.size,
                    });
                });
            });
        }
    }

    downloadAttachment(attachment: MediaApi): void {
        let headers = new HttpHeaders;
        headers = headers.set('content-type', attachment.mimeType);
        this.httpClient.get(attachment.url, {'headers': headers, responseType: 'blob'}).subscribe(
            result => {
                download.blob(result, attachment.name);
            }
        );
    }


    openAccountPreferences(): void {
        // For some reason, a click event handler is needed here so that the keyboard event can recognize the link as an event target
        // and the id of the mention prominence can be picked up, and clicking on the link won't trigger panel toggling
        // No need for the actual opening here as it is the default behavior of the link
    }

    tabChanged(tabIndex: number) {
        this.selectedTabIndex = tabIndex;
    }


    updatePreferredImage(image: ImageModel) {
        const images = this.mention.images.map(img => {
            const imgApiModel = img.toApiEntity();
            imgApiModel.isPreferred = img.source === image.source ? !image.isPreferred : false;
            return imgApiModel;
        });

        const patch: PartialMentionApiModel = {
            id: this.mention.mentionId,
            images
        };

        this.mentionsService.patchMention(patch);
    }


    /**
     * Sets the relevant properties to display critical mention
     */
    private setCriticalMentionFeatures(): void {
        if (!this.mention) {
            return;
        }

        this.isCriticalMention = Mention.isCriticalMention(this.mention);
        this.isLegacyStashedClip = this.isCriticalMention && !this.mention.criticalMentionWordPlayUrl;
        this.isCriticalMentionValid = true;
        this.setIsStashedClip();

        if (this.isCriticalMentionExpired() && !this.isStashedClip) {
            this.isCriticalMentionValid = false;
            this.criticalMentionFeedbackMessage = 'Mention.details.criticalMentionExpired';
        }

        if (this.isCriticalMention && !this.isSlimPlayerValid()) {
            this.criticalMentionFeedbackMessage = 'Mention.details.criticalMention.generalError';
            this.isCriticalMentionValid = false;
        }

        this.setCriticalMentionSlimPlayerURL();
    }


    private isCriticalMentionExpired(): boolean {
        if (!this.preferences || !this.preferences.timeZone || !this.mention) {
            return false;
        }

        if (Mention.isCriticalMention(this.mention)) {
            const timezone = this.preferences.timeZone;
            const published = moment(new Date(this.mention.datePublished)).tz(timezone);
            const now = moment().tz(timezone);

            const expiryDateInPerferedTimezone = published.add(this.totalValidDaysForCriticalMention, 'd');

            return expiryDateInPerferedTimezone.diff(now, 'd') <= 0;
        }

        return false;
    }

    private setIsStashedClip(): void {
        if (!this.isCriticalMention) {
            this.isStashedClip = false;

            return;
        }

        if (this.isLegacyStashedClip) {
            this.isStashedClip = true;

            return;
        }

        if (this.mention.criticalMentionWordPlayUrl) {
            const clipIdMatches = this.mention.criticalMentionWordPlayUrl.match(/clipid/i);
            this.isStashedClip = !!clipIdMatches && !!clipIdMatches.length;
        }
    }

    private isSlimPlayerValid(): boolean {
        let slimPlayerMatchUrl;

        if (!this.mention.url) {
            return false;
        }

        if (this.isStashedClip) {
            slimPlayerMatchUrl = this.mention.url.match(/\#\/clip\/slim\/.*/);
        } else {
            slimPlayerMatchUrl  = this.mention.url.match(/^https:\/\/([^\.]+)\.criticalmention\.com\/.*#\/clip\/search\/([^\?]+)?.*&token=([^\&]+?)(&|$)/);
        }

        return slimPlayerMatchUrl;
    }

    /**
     * Generates and sets the URL to embed this mention's Critical Mention player
    */
    private setCriticalMentionSlimPlayerURL(): void {
        let criticalMentionURL: string = '';

        if (this.isCriticalMention) {
            // mention.url is the slim player url for critical mentions
            criticalMentionURL = this.mention.url;
        } else {

            return;
        }

        if (this.criticalMentionSlimPlayerURL !== criticalMentionURL) {
            const url = new URL(criticalMentionURL);
            // the iframe doesnt recognize changes in the url hash, adding date before hash registers change
            url.searchParams.append('_', '' + +new Date());
            this.criticalMentionSlimPlayerURL = url.toString();
        }
    }

    /**
     * Requests embedable content from social networks
     */
    private getOEmbedCode() {
        if (!this.mention.mediaType.isSocial || !this.mention.url) {
            return;
        }

        let request: Observable<OEmbedRichResponseModel>;
        switch (this.mention.mediaType.id) {
            // @TODO NEP-32721 - facebook doesn't work right now because of a CORS problem
            // case MediaTypeEnum.FACEBOOK:
            //     request = this.oembedApi.facebook(this.mention.url);
            //     break;
            case MediaTypeEnum.TWITTER:
                request = this.oembedApi.twitter(this.mention.url);
                break;
            case MediaTypeEnum.TIKTOK:
                const videoId = this.mention.url.match(this.endOfUrl)?.[0];

                // eslint-disable-next-line max-len
                const tiktokBlock: any = `<blockquote class="tiktok-embed" cite="${this.mention.url}" data-video-id="${videoId}" style="max-width: 605px;min-width: 325px;" >
                    <section>
                    </section>
                </blockquote>
                <script async src="https://www.tiktok.com/embed.js"></script>`;

                this.socialEmbedCode = this.sanitizer.bypassSecurityTrustHtml(tiktokBlock);
                break;
            case MediaTypeEnum.INSTAGRAM:
                const instagramBlock: any = `<blockquote class="instagram-media" data-instgrm-captioned="" data-instgrm-version="13">
                    <a href="${this.mention.url}" target="_blank">Open on Instagram</a>
                    </blockquote><script src="//www.instagram.com/embed.js"></script>`;

                this.socialEmbedCode = this.sanitizer.bypassSecurityTrustHtml(instagramBlock);
                break;
            case MediaTypeEnum.BLUESKY:
                request = this.oembedApi.bluesky(this.mention.url);
                break;
        }

        if (request) {
            request.subscribe((response) => {
                this.socialEmbedCode = this.sanitizer.bypassSecurityTrustHtml(response.html);
            });
        }
    }

    private setCanLinkOut(): void {
        this.canLinkOut = true;

        if (this.mention.isLexisNexis) {
            this.canLinkOut = false;
            return;
        }

        if (
            this.mention.mediaType.id === MediaTypeEnum.TV
            || this.mention.mediaType.id === MediaTypeEnum.RADIO
        ) {
            if (Mention.isCriticalMention(this.mention)) {
                this.canLinkOut = false;
            }
        }
    }


    /**
     * Handles messages sent by the Critical Mention player to the parent window
    */
    private handleCriticalMentionMessage(message: CriticalMentionMessageModel): void {
        const hasMessage: boolean = message !== null && typeof message === 'object';
        const isSavingClip: boolean = hasMessage && message.action === criticalMentionMessageTypesEnum.create;
        const isUpdatingClip: boolean = hasMessage && message.action === criticalMentionMessageTypesEnum.update;

        if (isSavingClip) {
            this.saveCriticalMentionClip(message);
        } else if (isUpdatingClip) {
            this.updateCriticalMentionClip(message);
        }
    }

    /**
     * Begins the process of updating a mention based on a clip that
     * was archived in Critical Mention's Wordplay player
    */
    private updateCriticalMentionClip(message: CriticalMentionMessageModel): void {
        const mention: Mention = this.mention.clone();
        mention.body = message.text;
        mention.duration = Math.round((Number(message.endSecondMicro) - Number(message.startSecondMicro)) / 1000);
        this.mentionsService.upsertMention(mention);
        this.coreDialogService.close(this.iframeDialogKey);
    }


    /**
     * Saves a mention based on a clip that has been archived
     * in Critical Mention's Wordplay player
    */
    private saveCriticalMentionClip(message: CriticalMentionMessageModel): void {
        const mention: Mention = this.mention.clone();
        mention.body = message.text;
        mention.duration = Math.round((Number(message.endSecondMicro) - Number(message.startSecondMicro)) / 1000);
        mention.criticalMentionWordPlayUrl = this.getCriticalMentionWordPlayUrl(message);

        const io: TextInputDialogModel = {
            inputs: {
                heading: 'MentionDetails.saveClip',
                initialValue: mention.headline,
                okLabel: 'save',
                submit: (headline: string) => {
                    mention.headline = headline;

                    const saveRequest = this.mentionsService.upsertMention(mention, this.search);
                    saveRequest.subscribe(() => {
                        this.coreDialogService.close(this.iframeDialogKey);
                    });

                    return saveRequest;
                }
            }
        };

        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    /**
     * Gets the WordPlay URL for a given clip based on the provided
     * critical mention archive clip confirmation message
    */
    private getCriticalMentionWordPlayUrl(message: CriticalMentionMessageModel): string {
        const url = new URL(this.mention.criticalMentionWordPlayUrl);
        const shareUUIDMatches = message.directLink.match(/\/slim\/(.*)\?/);

        if (shareUUIDMatches) {
            // take the first group
            url.searchParams.append('shareUUID', shareUUIDMatches[1]);
        }

        url.searchParams.append('clipId', '' + message.clipId);

        return url.toString();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
